import React from "react";
import {
    Backdrop,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    makeStyles,
    MenuItem,
    Slider,
    TextField,
    Typography,
} from "@material-ui/core";
import { Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { Label } from "../../../components/formInputs";
import { ErrorHelper, SelectField, TextFieldCommon } from "../../../components/formikFormInputs";
import * as EstimatorActions from "../../../actions/estimator";
import * as EstimationActions from "../../../actions/estimation";
import * as Actions from "../../../actions/verify";
import { CallingFromObject, contributionFrequencyOpn, ESTIMATE_MAX_AGE, genderDropDown, healthDropdown, IUL710PayEnum, IUL710TermEnum, primaryPlansOptions, tobaccoDropdownOpn } from "../../../constants";
import { ageDropDown, joinAsFullName, maskCurrency, unmaskCurrency } from "../../../utils/commonUtils";
import { setOptions } from "../../client/Estimation/V1/Components/StepThreeTwo";
import { useActions } from "../../../actions";
import { getQueryStringValue } from "../../../utils/queryString";
import { useSelector } from "react-redux";
import { RootState } from "../../../reducers";
import { submitEstimationFormik, SubmitEstimationFormikProps, submitIULEstimation } from "../../client/Estimation/V1/Components/EstimationFormikHandler";
import { useCreateAllocationPayLoad } from "../../client/Estimation/Hooks/Hooks";
import Socket from "../../client/Estimation/V1/Socket";
import { createEstimateReports } from "../../../components/client/Proposal";
import { noop } from "lodash";
import TestingProcessDialog from "../../../components/client/Estimation/TestingProcessDialog";
// --- STYLING (using MUI's JSS) ---
const useStyles = makeStyles((theme) => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: "#000",
    },
    cardContainer: {
        border: "4px solid #fabe0e",
        borderRadius: "12px",
        padding: theme.spacing(2),
    },
    rightPanel: {
        backgroundColor: "#0096ff",
        color: "#ffffff",
        borderRadius: "8px",
        padding: theme.spacing(4),
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
    },
    resultSection: {
        marginBottom: theme.spacing(3),
        "&:last-child": {
            marginBottom: 0,
        },
    },
    resultValue: {
        fontWeight: "bold",
        fontSize: "2.5rem",
        lineHeight: 1.2,
    },
    resultLabel: {
        opacity: 0.9,
    },
    formLabel: {
        fontWeight: 500,
        marginBottom: 0,
        display: "block",
        color: "#ffffff",
    },
    calculateButton: {
        backgroundColor: "#0096ff",
        color: "#ffffff",
        borderRadius: "50px",
        padding: theme.spacing(1.5, 4),
        fontSize: "1rem",
        fontWeight: "bold",
        marginTop: theme.spacing(3),
        "&:hover": {
            backgroundColor: "#0077cc",
        },
    },
    sliderLabels: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: theme.spacing(-1),
    },
    darkField: {
        background: " #1b263b !important",
        border: "1px solid #cfcfcf",
        borderRadius: "30px",
    },

}));


type KaiZenCalculatorFormProps = {
    setEstimationResponse: Function,
    estimationResponse: any
};

const KaiZenCalculatorForm = (props: KaiZenCalculatorFormProps) => {
    const { setEstimationResponse, estimationResponse } = props;
    const classes = useStyles();
    const verifyInviteData = useSelector((state: RootState) => state.verify);
    const verifyActions = useActions(Actions);
    const inviteToken = getQueryStringValue("token");
    const user = verifyInviteData;
    const estimationActions = useActions(EstimationActions);
    const clientPolicy = user && user.client;
    const [issueAge, setIssueAge] = React.useState<number>((clientPolicy && clientPolicy.age) as number);
    const [estimatorData, setEstimatorData] = React.useState<any>(null);
    const [requestId, setRequestId] = React.useState<null | string>(null);
    const strategyId = clientPolicy && clientPolicy.strategy && clientPolicy.strategy.id;
    const carrierId = clientPolicy && clientPolicy.carrier && clientPolicy.carrier.id;
    const [isEstimationDataLoading, setIsEstimationDataLoading] = React.useState<boolean>(false);
    const minCon = useSelector((state: RootState) => state.minimumContribution) || 21350;
    // const [estimationResponse, setEstimationResponse] = React.useState<any>(null);
    const callingFrom: string = CallingFromObject.ENROLLMENT; 
    const estimatorActions = useActions(EstimatorActions);
    const createAllocationPayLoad = useCreateAllocationPayLoad((estimatorData && estimatorData.id), (clientPolicy && clientPolicy.carrier && clientPolicy.carrier.id), clientPolicy && clientPolicy.clientId, user as any, true);
    const [isReportDataLoading, setIsReportDataLoading] = React.useState<boolean>(false);
    const [isReportRequestCompleted, setIsReportRequestCompleted] = React.useState<boolean>(false);
    
    // --- VALIDATION SCHEMA (using Yup) ---
    const validationSchema = Yup.object().shape({
        beginDistributions: Yup.number().required("Start age is required"),
        contributionAmount:Yup.number().required("Contribution Amount is required"),
        // contributionAmount: Yup.string()
        //     .required("Contribution Amount is required")
        //     .test("min-amount", `Min Amount should be ${maskCurrency(minCon + "")}`, (value) => {
        //         if (!value) return false;
        //         const num = unmaskCurrency(value); // remove commas if formatted
        //         return !isNaN(num) && num >= minCon;
        //     }),
        contributionFrequency: Yup.string().required("Premium schedule is required"),
        endDistributions: Yup.number().required("End age is required").min(Yup.ref("beginDistributions"), "End age can't be less than start age"),
        gender: Yup.number().required("Sex is required"),
        healthType: Yup.string().required("Health status is required"),
        issueAge: Yup.number().required("Age is required").min(18).max(70),
        primaryFocusPlan: Yup.string().required("Contribution Years required"),
        tobacco: Yup.string().required("This field is required"),

    });

// fetch the estimation data from the server
    React.useEffect(() =>{
        (async ()=>{
            if(clientPolicy && clientPolicy.id ){
                const clientPolicyId = clientPolicy.id
                const userId = user && user.id
                const estimationDetails: any = await estimationActions.getEstimationDetailsByUserId(userId, callingFrom, clientPolicyId);
                if(estimationDetails){
                    const stressResponse: any = JSON.parse(estimationDetails && estimationDetails.stressResponse);
                    if(stressResponse && stressResponse.success){
                        setEstimationResponse(stressResponse.success);
                    }
                }
            }
        })()
    },[clientPolicy && clientPolicy.id, user.id])

    React.useEffect(() => {
            (async () => {
                // const response: any = await estimationActions.getIssueAge(client && client.dob, clientPolicy && clientPolicy.carrierId);
                if (user && user.dob && clientPolicy && clientPolicy.carrierId || true) {
                    // @todo need the carrierId after backend support
                    const response: any = await estimationActions.getIssueAge(user && user.dob, undefined);
                    setIssueAge(response && response.issueAge);
                }
            })();
    }, [clientPolicy && clientPolicy.dob, clientPolicy && clientPolicy.carrierId]);

    React.useEffect(() => {
        (async () => {
            if (carrierId && strategyId) {
                // Get estimator details for strategyId 1 (Kaizen)
                const res = await estimatorActions.getEstimatorDetails({ carrierId: carrierId, strategyId: strategyId });
                if (!!res) {
                    setEstimatorData(res);
                }
            }
        })()
    }, [carrierId, strategyId])

    React.useEffect(() => {
        async function vfAccount() {
            await verifyActions.verifyAccount(inviteToken);
        }
        vfAccount();
    }, []);


    const socketButtonClick = () => {
            const payload: any = {
                callingFrom: callingFrom,
                createAllocationPayLoad,
                estimationActions,
                initRequest: noop,
                onReportsAlreadyExist: () => setIsEstimationDataLoading(false),
                onRequestIdCreated: (id: any) => setRequestId(id),
                setIsEstimationDataLoading,
                setIsReportDataLoading,
                setIsReportRequestCompleted,
                setRequestId,
                runPublicly: true,
                user: {
                    id: user.id,
                    companyLinkUrl: clientPolicy && clientPolicy.company && clientPolicy.company.companyLinkUrl,
                    loggerInUserId: user.id,
                    clientPolicy,
                    clientId: clientPolicy.clientId,
                    role: user && user.role,
                },
            };
            createEstimateReports(payload).then();
        };

    const runKaizenEst = async (values) =>{
            // create user as per functions requirement
            const newUser = {
                ...user,
                accessType: "",
                client:  {
                    clientPolicies: user.client ? [user.client] : [],
                },
                clientId: user && user.client && user.client.id,
            }
    
            // for kaizen estimation
            const props = {
                values: {
                    healthType: values.healthType,
                    tobacco: values.tobacco,
                    primaryFocusPlan: "contributionAmount",
                    beginDistributions: values.beginDistributions,
                    endDistributions: values.endDistributions, 
                    contributionFrequency: values.contributionFrequency, 
                    gender: values.gender,
                    totalCombinedContribution: values.contributionAmount
                },
                role: user && user.role,
                setIsEstimationDataLoading,
                userId: user && user.id,
                setRequestId,
                userName: joinAsFullName(user as any),
                agentName: joinAsFullName(user as any),
                issueAge: issueAge,
                estimatorData,
                estimationActions,
                callingFrom,
                user: newUser,
                atlantic :false,
                clientPolicy,
                createAllocationPayLoad
            }
            submitEstimationFormik(
                    props,
                    undefined,
                    undefined,
                    undefined,
                    true
            )
            
        }

  
    //change initial values key according to form fields
    const initialValues = {
        issueAge: issueAge,
        gender: user && (user as any).gender,
        healthType: "",
        tobacco: "",
        // deathBenefit: 1000000,
        contributionAmount: 50000,
        primaryFocusPlan: "7-pay",
        premiumSchedule: "",
        beginDistributions: 65,
        endDistributions: 90,
    };

    const handleSubmit = (values, { setSubmitting }) => {
        runKaizenEst(values);
    };

    

    return (<>

        <Socket
            callingFrom={callingFrom}
            closeAfterFullProcess={false}
            handleButtonClick={socketButtonClick}
            handleReset={noop}
            handleResult={noop}
            isReportRequestAutomated={false}
            onCompleteReportDataLoading={noop}
            page={"enrollment"}
            requestId={requestId}
            setIsEstimationDataLoading={setIsEstimationDataLoading}
            setIsReportDataLoading={setIsReportDataLoading}
            setIsReportRequestCompleted={setIsReportRequestCompleted}
            setLifeDeathBenefitSeeking={noop}
            setResponseData={setEstimationResponse}
            userId={user && user.id}
        />
        {
            isEstimationDataLoading &&
            <Backdrop className={classes.backdrop} open={isEstimationDataLoading}>
                <TestingProcessDialog
                    getEstimationProgress={estimationActions.getEstimationProgress}
                    handleClose={() => setIsEstimationDataLoading(false)}
                    isEstimationDataLoading={isEstimationDataLoading}
                    open={isEstimationDataLoading}
                />
            </Backdrop>
        }
        <Formik
            initialValues={{ ...initialValues }}
            validationSchema={validationSchema}
            enableReinitialize={true}
            onSubmit={handleSubmit}
        >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
                <form onSubmit={handleSubmit}>
                    <div style={{ border: "1px solid rgb(7, 184, 248)", padding: 15, background: "#1b263b", borderRadius: 16 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}><Typography variant="caption" className="main-title">Customize Your Policy</Typography></Grid>
                            {/* --- LEFT SIDE: THE FORM --- */}
                            <FormFields issueAge={issueAge} />
                            <Grid item xs={12} container justify="center">
                                <Button type="submit" className={classes.calculateButton}>
                                    Calculate
                                </Button>
                            </Grid>
                        </Grid>
                    </div>
                </form>
            )}
        </Formik>
    </>
    );
};

export default KaiZenCalculatorForm;



const FormFields = (props: any) => {
    const { values, handleChange, setFieldValue, errors } = useFormikContext<any>();
    const [beginDistributions, setBeginDistributions] = React.useState(setOptions(ESTIMATE_MAX_AGE));
    const [endDistributions, setEndDistributions] = React.useState<any[]>(setOptions(ESTIMATE_MAX_AGE));
    const { issueAge } = props;
    const classes = useStyles();
    const [ageIncrement, setAgeIncrement] = React.useState(0);

    const formatCurrency = (value) => new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
    const formFields = [
        {
            label: <Label label={"Age"} required={true} />,
            name: "issueAge",
            component: SelectField,
            options: ageDropDown(),
            key: values.issueAge,
            grid: 6,
            size: "small",
            className: "darkField",
        },
        {
            label: <Label label={"Sex"} required={true} />,
            name: "gender",
            component: SelectField,
            options: genderDropDown,
            key: values.gender,
            grid: 6,
            size: "small",
            className: "darkField",
        },

        {
            label: <Label label={"Health"} required={true} />,
            name: "healthType",
            component: SelectField,
            options: healthDropdown.map((option: any) => ({ ...option, key: option.value })),
            grid: 6,
            size: "small",
            className: "darkField",
        },
        {
            label: <Label label={"Tobacco/Marijuana Use"} required={true} />,
            name: "tobacco",
            component: SelectField,
            options: tobaccoDropdownOpn,
            key: values.tobacco,
            grid: 6,
            size: "small",
            className: "darkField",
        },
        {
            label: <Label label={"Annual Contribution"} required={true} />,
            name: "contributionAmount",
            component: "slider",
            min: 22000,
            max: 250000,
            step: 1000,
            grid: 12,
            size: "small",
        },
        {
            label: <Label label={"Contribution Years"} required={true} />,
            name: "primaryFocusPlan",
            component: SelectField,
            onSelectChange: (value: string) => value === IUL710PayEnum.IUL7Pay ? setAgeIncrement(IUL710TermEnum.IUL7) : setAgeIncrement(IUL710TermEnum.IUL10),
            options: primaryPlansOptions,
            grid: 6,
            size: "small",
            className: "darkField",
        },
        {
            label: <Label label={"Premium Schedule"} required={true} />,
            name: "contributionFrequency",
            component: SelectField,
            options: contributionFrequencyOpn,
            grid: 6,
            size: "small",
            className: "darkField",
        },
        {
            label: <Label label={"Income Start Age"} required={true} />,
            name: "beginDistributions",
            component: SelectField,
            options: beginDistributions,
            grid: 6,
            size: "small",
            className: "darkField",
        },
        {
            label: <Label label={"Income End Age"} required={true} />,
            component: SelectField,
            name: "endDistributions",
            options: endDistributions,
            grid: 6,
            size: "small",
            className: "darkField",
        },
    ];

    React.useEffect(() => {
        if (!!issueAge) {
            const beginDistVal: number = issueAge + ageIncrement;
            const res: any[] = setOptions(beginDistVal);
            setBeginDistributions(res);
            setEndDistributions(res);
        }

    }, [issueAge, ageIncrement]);


    return (<>
        {formFields.map((field: any) => (
            <Grid item xs={12} sm={field.grid} key={field.name}>

                <TextFieldCommon
                    values={values}
                    onChange={handleChange}
                    InputLabelProps={{ disabled: false }}
                    {...field}
                />
                <ErrorHelper errors={errors} name={field.name} />

                {field.component === "slider" && (
                    <>
                        <Typography gutterBottom className={classes.formLabel}>
                            {field.label} <strong style={{ float: 'right' }}>{formatCurrency(values[field.name])}</strong>
                        </Typography>
                        <Slider
                            name={field.name}
                            value={values[field.name]}
                            onChange={(event, newValue) => setFieldValue(field.name, newValue)}
                            min={field.min}
                            max={field.max}
                            step={field.step}
                        />
                        <ErrorHelper errors={errors} name={field.name} />
                        <Box className={classes.sliderLabels}>
                            <Typography
                                variant="caption" style={{ color: "#cfcfcf" }}>{formatCurrency(field.min)}</Typography>
                            <Typography
                                variant="caption" style={{ color: "#cfcfcf" }}>{formatCurrency(field.max)}</Typography>
                        </Box>
                    </>
                )}
            </Grid>
        ))}
    
    </>);
}